import type { BotMode } from '../../types/leadBot.js';
import { isAllowlisted } from './allowlist.js';

// SMS Lead Bot — the send gate. Small on purpose: it is the ONE place that
// answers "may this go out without a human?", so there is one function to read
// when auditing the claim and one to change when the posture changes.
//
// ⚠️⚠️ THE TRIAL POSTURE (2026-08-23). Phase 0 was draft mode only. This build
// makes autonomous sending real, for an INTERNAL TRIAL, on the EXISTING SHARED
// BROADCAST NUMBER — and those two facts together are why the allowlist exists.
// See src/lib/leadBot/allowlist.ts for the specific failure it prevents.
//
// The invariant is no longer "a human approves everything". It is now:
//
//   NOTHING GOES OUT UNLESS EITHER a human approved it, OR `resolveAutonomy`
//   returned autoSend:true — and `resolveAutonomy` is the only thing that can
//   return that.
//
// ⚠️ DO NOT SCATTER MODE CHECKS THROUGH THE SEND PATH. Every autonomous send
// decision comes from here. A second `if (mode === 'autonomous')` somewhere
// else is how the allowlist gets bypassed by accident.

/** Why an autonomous send was refused. Each one is reported, never silent. */
export type AutonomyBlockReason =
  /** Ordinary draft mode — the reply waits for a person. Not a fault. */
  | 'draft_mode'
  /** ⚠️ Autonomous with nothing allowlisted. A MISCONFIGURATION, not a mode. */
  | 'empty_allowlist'
  /** Autonomous, but this particular number is not one we may text unreviewed. */
  | 'not_allowlisted';

export type AutonomyVerdict =
  | { autoSend: true }
  | { autoSend: false; reason: AutonomyBlockReason; detail: string };

export interface AutonomyInput {
  mode: BotMode;
  /** E.164 numbers this bot may text without review. */
  allowlist: readonly string[];
  /** The lead's number, E.164. */
  leadPhone: string;
}

/**
 * ⚠️⚠️ THE SINGLE CHOKEPOINT. The only function in the codebase that can say
 * "send this without a human".
 *
 * ⚠️ AN EMPTY ALLOWLIST IN AUTONOMOUS MODE REFUSES TO SEND. It does NOT mean
 * "no restriction". This is the load-bearing line: if empty meant "anyone",
 * going live to the public would be one checkbox away on a settings screen, and
 * the trial's safety would rest on nobody clearing a field. Instead, going live
 * takes TWO deliberate acts — a dedicated number and a cleared allowlist — and
 * the second one on its own just stops the bot rather than unleashing it.
 */
export function resolveAutonomy(input: AutonomyInput): AutonomyVerdict {
  if (input.mode !== 'autonomous') {
    return {
      autoSend: false,
      reason: 'draft_mode',
      detail: 'This bot is in draft mode — the reply is waiting for approval.',
    };
  }
  if (input.allowlist.length === 0) {
    return {
      autoSend: false,
      reason: 'empty_allowlist',
      detail:
        'This bot is set to autonomous but its test phone allowlist is empty, so it will ' +
        'not send anything. Add the numbers it may text, or put it back into draft mode.',
    };
  }
  if (!isAllowlisted(input.leadPhone, input.allowlist)) {
    return {
      autoSend: false,
      reason: 'not_allowlisted',
      detail:
        `${input.leadPhone} is not on this bot's test phone allowlist, so nothing will be ` +
        'sent to it automatically. Replies here still wait for approval.',
    };
  }
  return { autoSend: true };
}

/**
 * Whether a reply on this bot waits for a person. Kept as a named predicate
 * because the UI asks the same question the send path does, and two different
 * answers on one screen is how a control ends up lying about what will happen.
 *
 * ⚠️ It takes ONLY the mode, so it answers "is this bot supervised", not "will
 * THIS message send" — that second question is `resolveAutonomy`, and the send
 * path must ask that one.
 */
export function requiresHumanApproval(mode: BotMode): boolean {
  return mode !== 'autonomous';
}

/** Autonomous sending is built as of the 2026-08-23 trial build. */
export function autonomousSendingIsBuilt(): boolean {
  return true;
}

/**
 * What to tell the AM about a bot's mode. A silently-ignored setting reads as
 * the feature being broken, and an autonomous bot that cannot send is exactly
 * the state someone needs to be told about rather than left to discover.
 */
export function modeNotice(mode: BotMode, allowlistCount: number): string | null {
  if (mode !== 'autonomous') return null;
  if (allowlistCount === 0) {
    return (
      'This bot is autonomous but its test phone allowlist is empty, so it will not send ' +
      'anything. Add the numbers it may text, or put it back into draft mode.'
    );
  }
  return (
    `This bot replies AUTOMATICALLY, with no approval step, to the ${allowlistCount} ` +
    `allowlisted number${allowlistCount === 1 ? '' : 's'}. Anyone else still waits for a person.`
  );
}
